import { ReactNode, useMemo } from "react";
import { View } from "react-native";
import TrunkLeafNavigation, {
	useTrunkLeafNavigationRouter,
	useTrunkLeafNavigationScreen,
} from "src/components/TrunkLeafNavigation";
import { useInsideModal } from "src/modals";
import GatheringModal from "src/modals/configs/GatheringModal";
import CommentsThread from "../CommentsThread";
import { usePhotoCommentsThreadDimensions } from "../PhotoCommentsThread/PhotoCommentsThreadSkeleton";
import getGatheringUserTagSuggestions from "../_/helpers/getGatheringUserTagSuggestions";
import useGatheringCommentsThreadThemeFragment from "../_/hooks/useGatheringCommentsThreadThemeFragment";

interface LeafProps {
	gatheringId: string;
	photoId: string;
}

const TrunkCommentsThread = ({
	gatheringTheme,
}: {
	gatheringTheme: ReturnType<typeof useGatheringCommentsThreadThemeFragment>;
}) => {
	const { props, closeSelf } = useInsideModal<GatheringModal["GatheringCommentsThread"]>();
	const router = useTrunkLeafNavigationRouter();
	const currentScreen = useTrunkLeafNavigationScreen();

	return (
		<CommentsThread
			title="Chat"
			threadSpecifier={{
				gatheringId: props.gatheringId,
			}}
			isAdmin={props.isAdmin}
			displayMessageThreads={true}
			onClose={closeSelf}
			onPhotoThreadPress={({ photoId }) => {
				router?.navigateToLeaf({
					gatheringId: props.gatheringId,
					photoId,
				});
			}}
			isFocused={currentScreen?.currentScreen === "Trunk"}
			getUserTagSuggestions={getGatheringUserTagSuggestions}
			testID="GatheringMainComments"
			{...gatheringTheme}
		/>
	);
};
const LeafCommentsThread = ({
	gatheringId,
	photoId,
	gatheringTheme,
}: LeafProps & {
	gatheringTheme: ReturnType<typeof useGatheringCommentsThreadThemeFragment>;
}) => {
	const { props, closeSelf } = useInsideModal<GatheringModal["GatheringCommentsThread"]>();
	const router = useTrunkLeafNavigationRouter();
	const currentScreen = useTrunkLeafNavigationScreen();

	return (
		<CommentsThread
			title="Thread"
			threadSpecifier={{
				gatheringId,
				photoId,
			}}
			isAdmin={props.isAdmin}
			displayMessageThreads={false}
			onClose={closeSelf}
			onBack={() => {
				router?.navigateToTrunk();
			}}
			isFocused={currentScreen?.currentScreen === "Leaf"}
			getUserTagSuggestions={getGatheringUserTagSuggestions}
			testID="GatheringThreadComments"
			{...gatheringTheme}
		/>
	);
};

const GatheringCommentsThread = () => {
	const style = usePhotoCommentsThreadDimensions();
	const { props } = useInsideModal<GatheringModal["GatheringCommentsThread"]>();
	const gatheringTheme = useGatheringCommentsThreadThemeFragment({
		gatheringId: props.gatheringId,
	});

	const { renderTrunk, renderLeaf } = useMemo<{
		renderTrunk: () => ReactNode;
		renderLeaf: (props: LeafProps) => ReactNode;
	}>(() => {
		return {
			renderTrunk: () => <TrunkCommentsThread gatheringTheme={gatheringTheme} />,
			renderLeaf: ({ gatheringId, photoId }) => (
				<LeafCommentsThread
					gatheringId={gatheringId}
					photoId={photoId}
					gatheringTheme={gatheringTheme}
				/>
			),
		};
	}, [gatheringTheme]);

	return (
		<View style={style}>
			<TrunkLeafNavigation renderTrunkScreen={renderTrunk} renderLeafScreen={renderLeaf} />
		</View>
	);
};
export default GatheringCommentsThread;
